import _ from 'lodash'
import React from 'react'
import { connect } from 'react-redux'

import { makeRequestName } from 'mednet-cns/src/reducers/request'
import { FETCH_UPDATE_IN_NOTIFICATION_USAGE } from 'mednet-cns/src/reducers/questionUpdate'

import { Subtitle1, Subtitle2, Subtitle3 } from 'pharmacy/src/typography'
import { Icon } from 'pharmacy/src/display/icon'
import { IconWithPopup } from 'pharmacy/src/display/iconWithPopup'

import * as css from './lastUsedInfo.scss'

export const USAGE_STATES = {
  NOT_SENT: 'notSent',
  SENT: 'sent',
  UPDATED_SINCE_SENT: 'updatedSinceSent',
}

// A notification's send time is its real send timestamp, falling back to the build
// date (created) when no real send resolved (e.g. local sanitized data). Both are
// UTC datetimes.
const sendTimeOf = (notification) =>
  (notification || {}).sent || (notification || {}).created

// The notification that most recently went out on a channel, by send time. We pick
// the max explicitly rather than trust index [0]: the backend orders by build date,
// so if send order ever diverges from build order, [0] would not be the latest send.
// This one notification is the reference for BOTH the state below and the date the
// chip displays, so the two can never speak to different sends.
const latestSentNotification = (notifications) =>
  _.maxBy((notifications || []).filter(sendTimeOf), (notification) =>
    new Date(sendTimeOf(notification)).getTime()
  )

// Classifies a channel's usage from its notifications and the content's latest
// revision time, returning the state and the notification that reference send came
// from.
//   notSent          — never sent on this channel for this specialty
//   updatedSinceSent — sent, but the content was revised after its most recent send
//   sent             — sent and current
//
// A revision after the most recent send started — even mid-send, which is only a
// partial delivery — counts as updated-since-sent (purple), erring toward letting
// the editor resend; revised before it counts as delivered (gray).
export const classifyChannelUsage = (notifications, lastRevisedTime) => {
  const latestNotification = latestSentNotification(notifications)

  if (!latestNotification) {
    return { state: USAGE_STATES.NOT_SENT, latestNotification: null }
  }

  const lastSentTime = sendTimeOf(latestNotification)
  const state =
    lastRevisedTime && new Date(lastRevisedTime) > new Date(lastSentTime)
      ? USAGE_STATES.UPDATED_SINCE_SENT
      : USAGE_STATES.SENT

  return { state, latestNotification }
}

// Digest-only usage state, read off the `digests` slice of an update's usage record
// ({ digests, newsletters }). The "hide used updates" filter calls this; the chip
// goes through classifyChannelUsage directly so it can also show the send's date.
export const getDigestUsageState = (usageRecord, lastRevised) =>
  classifyChannelUsage(_.get(usageRecord, 'digests', []), lastRevised).state

class LastUsedInfo extends React.Component {
  // Keep just the YYYY-MM-DD date, whether the input is a full datetime
  // ("2025-05-02 11:00:00", newsletters) or already date-only ("2025-05-02",
  // the digest send date).
  trimDate = (dateStr) => (dateStr || '').slice(0, 10)

  // The content label ("answer"/"poll") names what the editor actually sees.
  // Every caller passes one; fall back to the generic "update" defensively.
  typeText = () => _.upperFirst(this.props.label || 'update')

  renderInfo() {
    const { notifications, specialtyTitle } = this.props || {}
    const { digests = [], newsletters = [] } = notifications || {}

    if (!digests.length && !newsletters.length) {
      return null
    }

    // Usage is scoped to the current specialty, so name it in the headers — the
    // same answer can have different history in another specialty.
    const inSpecialty = specialtyTitle ? ` in ${specialtyTitle}` : ''

    return (
      <div>
        {!digests.length ? null : (
          <div>
            <Subtitle1>Last 5 digests{inSpecialty}</Subtitle1>
            {digests.map((notification, i) => (
              <Subtitle2 key={i} className={css.row}>
                {this.trimDate(notification.sent || notification.created)}
              </Subtitle2>
            ))}
          </div>
        )}
        {!newsletters.length ? null : (
          <div>
            <Subtitle1>Last 5 newsletters{inSpecialty}</Subtitle1>
            {newsletters.map((notification, i) => (
              <Subtitle2 key={i} className={css.row}>
                {this.trimDate(notification.sent || notification.created)}
              </Subtitle2>
            ))}
          </div>
        )}
      </div>
    )
  }

  render() {
    const { lastUsedIsError, notifications, mode, className, lastRevised } =
      this.props

    let text = ''

    // notifications is the record for THIS update: undefined until its own fetch
    // batch returns, then { digests, newsletters } (possibly empty). hasRecord is
    // the per-item "has my data arrived" signal — reliable under overlapping
    // batches where the page-shared request flag isn't (it flips loaded when the
    // first batch returns, not this update's).
    const hasRecord = Boolean(notifications)
    const { digests = [], newsletters = [] } = notifications || {}

    // A small inline notice — this renders in a chip slot on a card. It must NOT
    // read as "not sent": a blank/absent chip would be a false "unused", so on
    // failure we say the check itself failed. The `!hasRecord` guard scopes this
    // to an item whose data never arrived — the request flag is shared across the
    // page, so a later, unrelated fetch erroring must not blank an item whose
    // record already loaded (mirrors the loading guard below).
    if (lastUsedIsError && !hasRecord) {
      return (
        <div className={className}>
          <Subtitle3 className={css.error}>Couldn’t check usage</Subtitle3>
        </div>
      )
    }

    // No record yet means this update's usage fetch is still in flight — every
    // rendered card is included in a batch keyed by its own updateId (dispatched
    // from the same search/digest response that produced the card), and a
    // successful receive seeds a record for every requested id, so a missing record
    // can only mean "not back yet", never "no fetch was made". Show the loader.
    //
    // We gate on the record's presence rather than the page-shared isLoaded/
    // isLoading flag on purpose: that flag flips to loaded when the FIRST of two
    // overlapping batches returns, so a card still awaiting a later batch would fall
    // through to a false "not sent" if we trusted it. Once the record exists we
    // never return here again, so data stays on screen through refetches without
    // flickering back to a spinner. Label the spinner so it isn't misread as "not
    // sent".
    if (!hasRecord) {
      return (
        <div className={className}>
          <Subtitle3 className={css.loading}>
            <Icon icon={['far', 'spinner']} size="small" /> Checking usage data…
          </Subtitle3>
        </div>
      )
    }
    // The channel the label speaks to depends on mode: a single channel for
    // newsletter/dailydigest, otherwise whichever channel sent it most recently.
    const latestDigest = latestSentNotification(digests)
    const latestNewsletter = latestSentNotification(newsletters)

    let channel
    if (mode === 'newsletter') {
      channel = 'newsletter'
    } else if (mode === 'dailydigest') {
      channel = 'digest'
    } else {
      const digestTime = latestDigest
        ? new Date(sendTimeOf(latestDigest))
        : null
      const newsletterTime = latestNewsletter
        ? new Date(sendTimeOf(latestNewsletter))
        : null
      channel = digestTime < newsletterTime ? 'newsletter' : 'digest'
    }

    // One classification for the chosen channel drives both the state and the date
    // the label shows, so the two always speak to the same send. The date comes
    // from the same notification the revised-since check is judged against — never a
    // separately-picked "newest" that could disagree.
    const channelNotifications =
      channel === 'newsletter' ? newsletters : digests
    const { state, latestNotification } = classifyChannelUsage(
      channelNotifications,
      lastRevised
    )
    const sendDateStr = latestNotification ? sendTimeOf(latestNotification) : ''

    // Three states, each a distinct chip color so a moderator scanning for
    // content worth adding can tell them apart without reading. Same info icon in
    // every state; the chip color (owned by this component's scss) carries the
    // meaning:
    //   pink   — not sent on this channel: fresh, worth adding
    //   gray   — sent and current: already seen, usually skip
    //   purple — sent, but revised since its most recent send: worth resending
    let chipClassName
    if (state === USAGE_STATES.NOT_SENT) {
      chipClassName = css.chipNotUsed
      text = `${this.typeText()} not sent in any ${channel} before`
    } else if (state === USAGE_STATES.UPDATED_SINCE_SENT) {
      chipClassName = css.chipUpdated
      text = `${this.typeText()} updated since it was sent in ${channel} on ${this.trimDate(
        sendDateStr
      )}`
    } else {
      chipClassName = css.chipUsed
      text = `${this.typeText()} previously sent in ${channel} on ${this.trimDate(
        sendDateStr
      )}`
    }

    return (
      <IconWithPopup
        isChip
        chipClassName={chipClassName}
        text={text}
        className={className}
      >
        {this.renderInfo()}
      </IconWithPopup>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const { updateId, specialtyId } = ownProps
  const lastUsedUpdateRequest =
    _.get(
      state.request.requests,
      makeRequestName(FETCH_UPDATE_IN_NOTIFICATION_USAGE)
    ) || {}

  // Pass the record through as-is (may be undefined): the component distinguishes
  // "no record yet" (still loading this update's batch) from a fetched-but-empty
  // record ("not sent"). Collapsing undefined to {} here would erase that
  // distinction and let a pending card render a false "not sent".
  const updateNotifications = _.get(
    state.questionUpdate.notifications,
    `["${specialtyId}"]["${updateId}"]`
  )

  return {
    notifications: updateNotifications,
    lastUsedIsError: lastUsedUpdateRequest.isError,
    errorStatus: lastUsedUpdateRequest.errorStatus,
  }
}

export const LastUsedInfoConnected = connect(mapStateToProps)(LastUsedInfo)
